

























































































































































































import { useAxios, useFilters } from '@/app/composable';
import store from '@/app/store';
import {
    ArrowRightIcon,
    ClockIcon,
    EyeIcon,
    PlusCircleIcon,
    ReceiptRefundIcon,
    RefreshIcon,
} from '@vue-hero-icons/outline';
import {
    CalendarIcon,
    CollectionIcon,
    DatabaseIcon,
    SwitchHorizontalIcon,
    TagIcon,
    UserIcon,
} from '@vue-hero-icons/solid';
import { computed, defineComponent, PropType, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import * as R from 'ramda';
import { ReimbursementMethodIcon } from '..';
import { ContractsAPI } from '../../api';
import { useContractStatus } from '../../composable';
import { ContractStatus, DerivationStatus, ReimbursementMethod, ReimbursementMethodShortText } from '../../constants';

export default defineComponent({
    name: 'ContractOverview',
    components: {
        UserIcon,
        CalendarIcon,
        SwitchHorizontalIcon,
        CollectionIcon,
        DatabaseIcon,
        ClockIcon,
        PlusCircleIcon,
        ReceiptRefundIcon,
        ReimbursementMethodIcon,
        EyeIcon,
        RefreshIcon,
        ArrowRightIcon,
        TagIcon,
    },
    props: {
        contract: {
            type: Object,
            required: true,
        },
        contractIdsWithActiveDerivation: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
    },
    setup(props, { emit, root }) {
        const { formatDate, formatDecimals } = useFilters();
        const { statusClass } = useContractStatus();
        const user = computed(() => store.state.auth.user);
        const isLegalRepresentative = ref<boolean>(user.value.roles.includes('legal-representative'));
        const isConsumer = ref<boolean>(props.contract.consumerOrg.id === user.value.organisationId);
        const { exec, loading } = useAxios(true);

        const uniqueAssets: any = computed(() => R.uniq(props.contract.assets));
        const isBundle = computed(() => props.contract.type === 'bundle');
        const isDerivation = computed(() => props.contract.type === 'derivation');
        const originalAsset = computed(() =>
            props.contract.assets.find((asset: { order: number }) => asset.order === 0),
        );
        const secondaryAsset = computed(() =>
            props.contract.assets.find((asset: { order: number }) => asset.order === 1),
        );
        const hasCost = computed(() => !!props.contract?.metadata?.pricing?.cost);

        const isExpired = computed(
            () =>
                props.contract.status === 'expired' ||
                (!R.isNil(props.contract.validUntil) && new Date() > new Date(props.contract.validUntil)),
        );

        const isDerivationFinalised = computed(
            () =>
                isDerivation.value &&
                [DerivationStatus.Active, DerivationStatus.Expired].includes(props.contract.status),
        );

        const canExtendOrRevise = computed(
            () =>
                props.contract.status === ContractStatus.Active &&
                !isBundle.value &&
                !isDerivation.value &&
                isLegalRepresentative.value &&
                !props.contract.inUpdate &&
                props.contract.type !== 'public' &&
                !isExpired.value,
        );

        const reimbursementText = computed(
            () =>
                ReimbursementMethodShortText[
                    props.contract.metadata.pricing.reimbursementMethod as ReimbursementMethod
                ],
        );

        const getUser = (type: string) => {
            if (type === 'provider') return props.contract.providerOrg.businessName;
            if (type === 'consumer') return props.contract.consumerOrg.businessName;
            return null;
        };

        const goToContract = () => {
            if (isDerivation.value)
                root.$router.push({ name: 'derivation-contract-details', params: { id: `${props.contract.id}` } });
            else
                root.$router.push({
                    name: 'contract-details',
                    params: { type: isBundle.value ? 'bundle' : 'details', id: `${props.contract.id}` },
                });
        };

        const goToContractMonitoring = () => {
            root.$router.push({
                name: 'contract-monitoring',
                params: { type: props.contract.type, id: `${props.contract.id}` },
            });
        };

        const configureContract = () => {
            emit('configure', props.contract);
        };

        const goToContractAction = (action: string) => {
            root.$router.push({
                name: 'contract-action',
                params: { type: isBundle.value ? 'bundle' : 'details', id: `${props.contract.id}`, action },
            });
        };

        const syncContract = (contractId: string) => {
            exec(ContractsAPI.syncContract(contractId, props.contract.type))
                .then((res: any) => {
                    const newContract = res.data;
                    if (!R.isNil(newContract.pendingTransactionId))
                        (root as any).$toastr.w(
                            'Blockchain transaction is still pending. Please try again later.',
                            'Pending Transaction',
                        );
                    else {
                        (root as any).$toastr.s('Contract state synchronised!', 'Success');
                        emit('reload');
                    }
                })
                .catch(() => {
                    (root as any).$toastr.e('Failed to synchronise contract state!', 'Error');
                });
        };

        return {
            isBundle,
            isDerivation,
            isExpired,
            hasCost,
            statusClass,
            formatDate,
            configureContract,
            goToContract,
            dayjs,
            getUser,
            uniqueAssets,
            canExtendOrRevise,
            reimbursementText,
            ReimbursementMethod,
            goToContractAction,
            goToContractMonitoring,
            isConsumer,
            user,
            isLegalRepresentative,
            loading,
            syncContract,
            secondaryAsset,
            originalAsset,
            isDerivationFinalised,
            formatDecimals,
        };
    },
});
